import ErrorText from '@/components/ErrorText';
import Input from '@/components/Input/Input';
import Label from '@/components/Label';
import Modal from '@/components/Modal/Modal';
import Button, { ButtonSizes, ButtonVariants } from '@/components/new-design/Button';
import { yupResolver } from '@hookform/resolvers/yup';
import { BaseSyntheticEvent, FC } from 'react';
import { useForm } from 'react-hook-form';
import { isAddress } from 'viem';
import * as yup from 'yup';
import { withdrawUsdt } from '../web3/withdrawUsdt';
import { useAccountAbstractionStore } from '@/features/account-abstraction/store/AccountAbstractionContext';
import { useReducer } from 'react';
import LoadingSpinner from '@/components/LoadingSpinner/LoadingSpinner';
import config from '@/common/config';
import { LiaExternalLinkAltSolid } from 'react-icons/lia';
import { toast } from '@/components/toast/useToast';
import { usePostTransfer } from '@/services/wallets/queries/usePostTransfer';
import { formatCurrency } from '@/pages/table/utils/utils';

interface State {
  loading: boolean;
  error: boolean;
  errorMessage: string | null;
  success: boolean;
  txHash: string | null;
}

type Action =
  | { type: 'START' }
  | { type: 'SUCCESS'; payload: string }
  | { type: 'ERROR'; payload: string }
  | { type: 'RESET' };

const initialState: State = {
  loading: false,
  error: false,
  errorMessage: null,
  success: false,
  txHash: null,
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'START':
      return { ...state, loading: true, error: false, errorMessage: null };
    case 'SUCCESS':
      return {
        ...state,
        loading: false,
        success: true,
        txHash: action.payload,
      };
    case 'ERROR':
      return {
        ...state,
        loading: false,
        error: true,
        errorMessage: action.payload,
      };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

interface TransferModalProps {
  open: boolean;
  setClose: () => void;
  balance: string;
}
interface FormInputs {
  amount: number;
  walletAddress: string;
}
export const validationSchema = (balance: number) =>
  yup
    .object({
      amount: yup
        .number()
        .required('Amount is required')
        .test('balance', 'Insufficient balance', (value) => !!value && value <= balance)
        .transform((value) => {
          if (isNaN(value)) {
            return 0;
          }
          return value;
        }),
      walletAddress: yup
        .string()
        .test('address', 'Enter valid address', (value) => !!value && isAddress(value))
        .required('Wallet Address is required'),
    })
    .required();

export const TransferModal: FC<TransferModalProps> = ({ open, setClose, balance }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const accountAbstractionStore = useAccountAbstractionStore();
  const { mutate: postTransfer } = usePostTransfer();

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
    reset,
  } = useForm<FormInputs>({
    resolver: yupResolver(validationSchema(Number(balance))),
    mode: 'onChange',
  });

  // const {signMessage} = useSignMessage()

  const onSubmit = async (data: FormInputs, e?: BaseSyntheticEvent<object, any, any>) => {
    e?.preventDefault();
    dispatch({ type: 'START' });
    try {
      const tx = await withdrawUsdt(accountAbstractionStore.state, data.walletAddress as `0x${string}`, data.amount);
      if (tx?.success) {
        dispatch({ type: 'SUCCESS', payload: tx.receipt.transactionHash });
        postTransfer({
          isSelfTransfer: false,
          txId: tx.receipt.transactionHash,
          from: tx.receipt.from,
          to: tx.receipt.to,
          amount: data.amount,
        });
      }
    } catch (e) {
      if (e instanceof Error) {
        dispatch({ type: 'ERROR', payload: e.message });
      } else dispatch({ type: 'ERROR', payload: '' });
    }
  };

  const onClose = () => {
    if (state.loading) return;
    dispatch({ type: 'RESET' });
    reset();
    setClose();
  };
  const handleCopyToClipboard = () => {
    toast({ description: 'Copied to clipboard', variant: 'success' });
    return navigator.clipboard.writeText(state.txHash || '');
  };
  const handleRetry = () => {
    dispatch({ type: 'RESET' });
  };

  return (
    <Modal open={open} setClose={onClose} className="lg:min-w-[500px]">
      <div className="left-4 flex w-full items-center justify-center lg:w-[500px]">
        <div className="flex h-full w-full flex-col gap-5 rounded-[30px] bg-ph-background-dark p-1 lg:p-8">
          <h2 className="font-manrope text-2xl font-semibold text-ph-gray-white">Withdraw USDT</h2>
          {!state.success && !state.error && (
            <>
              <span>Make sure you are sending USDT to a wallet supporting Polygon</span>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="flex flex-col gap-2">
                  <div className="flex flex-col gap-1">
                    <Label semiBold>Polygon Wallet Address</Label>
                    <Input
                      placeholder="0x"
                      {...register('walletAddress')}
                      error={!!errors.walletAddress}
                      type="text"
                      className="w-full"
                    />
                    {errors.walletAddress && <ErrorText>* {errors.walletAddress.message}</ErrorText>}
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label semiBold>Amount in USDT</Label>

                    <Input
                      placeholder="0.00"
                      {...register('amount')}
                      error={!!errors.amount}
                      type="number"
                      className="w-full"
                    />
                    {errors.amount?.message && <ErrorText>* {errors.amount.message}</ErrorText>}
                    <div className="mr-auto text-right text-xs font-semibold leading-[19px] text-ph-gray-evening">
                      Balance: ${formatCurrency(Number(balance))}
                    </div>
                  </div>

                  <div className="mt-4 flex gap-1">
                    <Button
                      disabled={!isDirty || !isValid || state.loading}
                      variant={ButtonVariants.primary}
                      size={ButtonSizes.lg}
                    >
                      {state.loading ? (
                        <span className="flex items-center gap-2">
                          <LoadingSpinner size="5" /> Processing
                        </span>
                      ) : (
                        'Withdraw'
                      )}
                    </Button>
                    <Button onClick={onClose} variant={ButtonVariants.secondary} size={ButtonSizes.lg}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </form>
            </>
          )}
          {state.error && (
            <div className="flex flex-col gap-5">
              {/* <h3 className="text-ph-red text-lg">!</h3> */}

              <div className="flex flex-col gap-1">
                <p className="text-sm font-bold text-ph-red">Transaction Failed:</p>
                <span className="flex items-center gap-1">
                  {state.errorMessage ? (
                    <p
                      title={state.errorMessage || ''}
                      className="overflow-hidden overflow-ellipsis text-sm text-ph-red"
                    >
                      {state.errorMessage}
                    </p>
                  ) : (
                    <p className="overflow-hidden overflow-ellipsis text-sm text-ph-red">
                      There was an error with your transaction. If this keeps happening please contact{' '}
                      <a type="email" className="text-ph-blue" href="mailto:support@phenompoker.com">
                        support@phenompoker.com
                      </a>
                    </p>
                  )}
                </span>
              </div>

              <div className="flex gap-1">
                <Button onClick={handleRetry} variant={ButtonVariants.primary} size={ButtonSizes.lg}>
                  Try Again
                </Button>
                <Button onClick={onClose} variant={ButtonVariants.secondary} size={ButtonSizes.lg}>
                  Close
                </Button>
              </div>
            </div>
          )}
          {state.success && (
            <div className="flex flex-col gap-5">
              <h3 className="text-lg text-ph-gray-white">Transaction Submitted!</h3>

              <a
                title={`${config.blockExplorerUrl}/tx/${state.txHash}`}
                href={`${config.blockExplorerUrl}/tx/${state.txHash}`}
                target="_blank"
                rel="noreferrer"
                className="text-ph-blue"
              >
                <span className="flex items-center gap-1">
                  <p>View on Block Explorer </p>
                  <LiaExternalLinkAltSolid size={20} className="text-ph-blue" />
                </span>
              </a>
              <div className="flex gap-1">
                <Button onClick={onClose} variant={ButtonVariants.secondary} size={ButtonSizes.lg}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
